import type { MessengerChatRate } from '@widgets/messenger/types/chat/chat'
import type { WidgetUploadedAttachment } from '@widgets/shared/types'

export enum MessengerMessageStatus {
  QUEUE = 'q',
  SENDING_ERROR = 'e',
  DELIVERED = 'd',
}

export type MessengerMessageListItemBase<T> = {
  type: T
  id: number | string
  messageTime: string // ISO, example: 2024-08-13T03:17:03+0000
}

export type MessengerSentMessageListItem = MessengerMessageListItemBase<'sent'> & {
  text: string
  attachments: WidgetUploadedAttachment[]
  status: MessengerMessageStatus
  trackId: string
}

export type MessengerIncomingMessageListItem = MessengerMessageListItemBase<'incoming'> & {
  text: string
  attachments: WidgetUploadedAttachment[]
  firstName: string // agent first name
  lastName: string // agent last name
}

export type MessengerInitialMessageListItem = MessengerMessageListItemBase<'initial'> & {
  name: string
  email: string
  text: string
}

export type MessengerAutoReplyMessageListItem = MessengerMessageListItemBase<'autoReply'> & {
  text: string
}

export type MessengerUserJoinedLogMessageListItem = MessengerMessageListItemBase<'log'> & {
  logType: 'userJoined'
  fullName: string
}

export type MessengerUserLeftLogMessageListItem = MessengerMessageListItemBase<'log'> & {
  logType: 'userLeft'
  fullName: string
  all: boolean // if all agents left the chat
}

export type MessengerChatRatedLogMessageListItem = MessengerMessageListItemBase<'log'> & {
  logType: 'chatRated'
  rate: MessengerChatRate
}

export type MessengerCommentLeftLogMessageListItem = MessengerMessageListItemBase<'log'> & {
  logType: 'commentLeft'
  text: string
}

export type MessengerMessageLogListItem =
  | MessengerUserJoinedLogMessageListItem
  | MessengerUserLeftLogMessageListItem
  | MessengerChatRatedLogMessageListItem
  | MessengerCommentLeftLogMessageListItem

export type MessengerMessageListItem =
  | MessengerSentMessageListItem
  | MessengerIncomingMessageListItem
  | MessengerInitialMessageListItem
  | MessengerAutoReplyMessageListItem
  | MessengerMessageLogListItem

export type MessengerLastMessage =
  | MessengerInitialMessageListItem
  | MessengerSentMessageListItem
  | MessengerIncomingMessageListItem

export const isSentMessageListItem = (item: MessengerMessageListItem): item is MessengerSentMessageListItem => {
  return item.type === 'sent'
}

export const isIncomingMessageListItem = (item: MessengerMessageListItem): item is MessengerIncomingMessageListItem => {
  return item.type === 'incoming'
}

export const isInitialMessageListItem = (item: MessengerMessageListItem): item is MessengerInitialMessageListItem => {
  return item.type === 'initial'
}

export const isAutoReplyMessageListItem = (
  item: MessengerMessageListItem,
): item is MessengerAutoReplyMessageListItem => {
  return item.type === 'autoReply'
}

export const isLogMessageListItem = (item: MessengerMessageListItem): item is MessengerMessageLogListItem => {
  return item.type === 'log'
}

export const isUserJoinedLogMessageListItem = (
  item: MessengerMessageListItem,
): item is MessengerUserJoinedLogMessageListItem => {
  return isLogMessageListItem(item) && item.logType === 'userJoined'
}

export const isUserLeftLogMessageListItem = (
  item: MessengerMessageListItem,
): item is MessengerUserLeftLogMessageListItem => {
  return isLogMessageListItem(item) && item.logType === 'userLeft'
}

export const isChatRatedLogMessageListItem = (
  item: MessengerMessageListItem,
): item is MessengerChatRatedLogMessageListItem => {
  return isLogMessageListItem(item) && item.logType === 'chatRated'
}

export const isCommentLeftLogMessageListItem = (
  item: MessengerMessageListItem,
): item is MessengerCommentLeftLogMessageListItem => {
  return isLogMessageListItem(item) && item.logType === 'commentLeft'
}
